import moment from 'moment'
import React, { useEffect, useState } from 'react'
import Moment from 'react-moment'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import InboundChevronIcon from '../../icons/chevron.svg?react'
import InboundBookingIcon from '../../icons/msg_inbound_booking.svg?react'
import InboundClassIcon from '../../icons/msg_inbound_class.svg?react'
import InboundConferenceIcon from '../../icons/msg_inbound_conference.svg?react'
import InboundFormIcon from '../../icons/msg_inbound_form.svg?react'
import InboundGiftIcon from '../../icons/msg_inbound_gift.svg?react'
import InboundInvoiceIcon from '../../icons/msg_inbound_invoice.svg?react'
import InboundPackageIcon from '../../icons/msg_inbound_package.svg?react'
import InboundPaymentIcon from '../../icons/msg_inbound_payment.svg?react'
import InboundPersonIcon from '../../icons/msg_inbound_person.svg?react'
import InboundPhoneIcon from '../../icons/msg_inbound_phone.svg?react'
import InboundSignatureIcon from '../../icons/msg_inbound_signature.svg?react'
import InboundSubscriptionIcon from '../../icons/msg_inbound_subscription.svg?react'
import InboundTaskIcon from '../../icons/msg_inbound_task.svg?react'
import OutboundBookingIcon from '../../icons/msg_outbound_booking.svg?react'
import OutboundClassIcon from '../../icons/msg_outbound_class.svg?react'
import OutboundConferenceIcon from '../../icons/msg_outbound_conference.svg?react'
import OutboundFormIcon from '../../icons/msg_outbound_form.svg?react'
import OutboundGiftIcon from '../../icons/msg_outbound_gift.svg?react'
import OutboundInvoiceIcon from '../../icons/msg_outbound_invoice.svg?react'
import OutboundPackageIcon from '../../icons/msg_outbound_package.svg?react'
import OutboundPaymentIcon from '../../icons/msg_outbound_payment.svg?react'
import OutboundPersonIcon from '../../icons/msg_outbound_person.svg?react'
import OutboundPhoneIcon from '../../icons/msg_outbound_phone.svg?react'
import OutboundSignatureIcon from '../../icons/msg_outbound_signature.svg?react'
import OutboundSubscriptionIcon from '../../icons/msg_outbound_subscription.svg?react'
import OutboundTaskIcon from '../../icons/msg_outbound_task.svg?react'
import OutboundChevronIcon from '../../icons/white_chevron.svg?react'
import { Message } from '../../models/Message'
import { RecordFactory } from '../../models/RecordFactory'
import { currencyFormat } from '../../utils/numbers'

interface MediaMessageBubbleProps {
    isOutbound: boolean
    message: Message
}

type MessageData = {
    title: string
    subtitle?: string
    icon?: React.ReactNode
}

export const MediaMessageBubble: React.FC<MediaMessageBubbleProps> = ({ isOutbound, message }) => {
    const navigate = usePSNavigate()
    const [messageData, setMessageData] = useState<MessageData>({ title: message.message || '' })

    useEffect(() => {
        const fetchMessageData = async () => {
            const data = await getMessageText(message)
            setMessageData(data)
        }

        fetchMessageData()
    }, [message])

    const getMessageText = async (message: Message): Promise<MessageData> => {
        if (!message.id) return { title: message.message || '' }

        switch (message.type) {
            case 'payment':
                const payment = message.payment?.id
                    ? await RecordFactory.getInstance().loadPayment(message.payment.id)
                    : null
                if (payment?.id) {
                    return {
                        title: `${currencyFormat(payment.amount)} payment`,
                        subtitle: payment.refnum,
                        icon: isOutbound ? <OutboundPaymentIcon /> : <InboundPaymentIcon />,
                    }
                }
                break
            case 'charge':
                const charge = message.charge?.id
                    ? await RecordFactory.getInstance().loadCharge(message.charge.id)
                    : null
                if (charge?.id) {
                    return {
                        title: `${currencyFormat(charge.chargeAmount)} Charge`,
                        subtitle: `Marked paid`,
                        icon: isOutbound ? <OutboundPaymentIcon /> : <InboundPaymentIcon />,
                    }
                }
                break
            case 'subscription':
                const subscription = message.subscription?.id
                    ? await RecordFactory.getInstance().loadSubscription(message.subscription.id)
                    : null
                if (subscription?.id)
                    return {
                        title: `${moment(subscription.date).format('M/D')} subscription`,
                        subtitle: message.message,
                        icon: isOutbound ? (
                            <OutboundSubscriptionIcon />
                        ) : (
                            <InboundSubscriptionIcon />
                        ),
                    }
                break
            case 'signature':
                const signature = message.signature?.id
                    ? await RecordFactory.getInstance().loadSignature(message.signature.id)
                    : null
                if (signature?.id) {
                    return {
                        title: signature.contract?.name || 'Contract',
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundSignatureIcon /> : <InboundSignatureIcon />,
                    }
                }
                break
            case 'record':
                const record = message.record?.id
                    ? await RecordFactory.getInstance().loadRecord(message.record.id)
                    : null
                if (record?.id) {
                    return {
                        title: record.type?.name || 'Form',
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundFormIcon /> : <InboundFormIcon />,
                    }
                }
                break
            case 'booking':
                const booking = message.booking?.id
                    ? await RecordFactory.getInstance().loadBooking(message.booking.id)
                    : null
                if (booking?.id)
                    return {
                        title: moment(booking.date).format('M/D'),
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundBookingIcon /> : <InboundBookingIcon />,
                    }
                break
            case 'signup':
                const contact = message.contact?.id
                    ? await RecordFactory.getInstance().loadUser(message.contact.id)
                    : null
                if (contact?.id)
                    return {
                        title: contact.name || 'Contact',
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundPersonIcon /> : <InboundPersonIcon />,
                    }
                break
            case 'estimate':
                const estimate = message.estimate?.id
                    ? await RecordFactory.getInstance().loadEstimate(message.estimate.id)
                    : null
                if (estimate?.id)
                    return {
                        title: `Estimate #${estimate.number}`,
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundInvoiceIcon /> : <InboundInvoiceIcon />,
                    }
                break
            case 'invoice':
                const invoice = message.invoice?.id
                    ? await RecordFactory.getInstance().loadInvoice(message.invoice.id)
                    : null
                if (invoice?.id)
                    return {
                        title: `Invoice #${invoice.number}`,
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundInvoiceIcon /> : <InboundInvoiceIcon />,
                    }
                break
            case 'task':
                const task = message.task?.id
                    ? await RecordFactory.getInstance().loadTask(message.task.id)
                    : null
                if (task?.id)
                    return {
                        title: task.title || 'Task',
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundTaskIcon /> : <InboundTaskIcon />,
                    }
                break
            case 'gift':
                const gift = message.gift?.id
                    ? await RecordFactory.getInstance().loadGift(message.gift.id)
                    : null
                if (gift?.id)
                    return {
                        title: gift.name || 'Gift',
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundGiftIcon /> : <InboundGiftIcon />,
                    }
                break
            case 'package':
                const pack = message.aPackage?.id
                    ? await RecordFactory.getInstance().loadPackage(message.aPackage.id)
                    : null
                if (pack?.id)
                    return {
                        title: `${moment(pack.createdDate).format('M/D')} package`,
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundPackageIcon /> : <InboundPackageIcon />,
                    }
                break
            case 'lesson':
                const lesson = message.lesson?.id
                    ? await RecordFactory.getInstance().loadClass(message.lesson.id)
                    : null
                if (lesson?.id)
                    return {
                        title: `${moment(lesson.date).format('M/D')} class`,
                        subtitle: message.message,
                        icon: isOutbound ? <OutboundClassIcon /> : <InboundClassIcon />,
                    }
                break
            case 'conference':
                return {
                    title: 'Video conference',
                    subtitle: message.message,
                    icon: isOutbound ? <OutboundConferenceIcon /> : <InboundConferenceIcon />,
                }
            case 'call':
                return {
                    title: 'Phone call',
                    subtitle: message.message,
                    icon: isOutbound ? <OutboundPhoneIcon /> : <InboundPhoneIcon />,
                }
            default:
                return { title: message.message || '' }
        }

        return { title: message.message || '' }
    }

    const getId = (message: Message): string | undefined | null => {
        switch (message.type) {
            case 'payment':
                return message.payment?.id
            case 'charge':
                return message.charge?.id
            case 'booking':
                return message.booking?.id
            case 'signature':
                return message.signature?.id
            case 'signup':
                return message.person?.id
            case 'subscription':
                return message.subscription?.id
            case 'estimate':
                return message.estimate?.id
            case 'invoice':
                return message.invoice?.id
            case 'record':
                return message.record?.id
            case 'task':
                return message.task?.id
            case 'lesson':
                return message.lesson?.id
            case 'gift':
                return message.gift?.id
            case 'package':
                return message.aPackage?.id
            default:
                return null
        }
    }

    const viewMedia = (message: Message) => {
        const id = getId(message)
        if (!id) {
            console.log(`Unlinked media type=${message.type}`)
            return
        }

        if (message.type === 'signup') navigate(`/clients/${id}`)
        else if (message.type === 'booking') navigate(`/bookings/${id}`)
        else if (message.type === 'order') navigate(`/orders/${id}`)
        else if (message.type === 'invoice') navigate(`/invoices/${id}`)
        // else if (message.type === 'royalty') navigate(`/royalties/${id}`)
        else if (message.type === 'estimate') navigate(`/estimates/${id}`)
        else if (message.type === 'package') navigate(`/packages/${id}`)
        else if (message.type === 'gift') navigate(`/gifts/${id}`)
        else if (message.type === 'lesson') navigate(`/lessons/${id}`)
        else if (message.type === 'signature') navigate(`/contracts/${id}`)
        else if (message.type === 'subscription') navigate(`/subscriptions/${id}`)
        // else if (message.type === 'task') navigate(`/tasks/${id}`)
        else if (message.type === 'record') navigate(`/forms/${id}`)
        else if (message.type === 'payment') navigate(`/payments/${id}`)
        else if (message.type === 'charge') navigate(`/charges/${id}`)
        // else if (message.type === 'review') navigate(`/reviews/${id}`)

        console.log(`Unlinked media type=${message.type}`)
    }

    const id = getId(message)
    const isLink = message.type && message.type !== 'person'

    const chevronImage = id ? (
        isOutbound ? (
            <OutboundChevronIcon className="chevron" />
        ) : (
            <InboundChevronIcon className="chevron" />
        )
    ) : null

    const { title, subtitle, icon } = messageData

    const calendarStrings = {
        sameDay: 'ddd, MMM Do YYYY h:mm a',
        lastDay: 'ddd, MMM Do YYYY h:mm a',
        lastWeek: 'ddd, MMM Do YYYY h:mm a',
        sameElse: 'ddd, MMM Do YYYY h:mm a',
    }

    return (
        <div className={isOutbound ? 'outboundMessage' : 'inboundMessage'}>
            <div
                className="message"
                onClick={() => viewMedia(message)}
                style={{ cursor: isLink && id ? 'pointer' : 'default' }}
            >
                <div className="messageMedia">
                    <div className="icon">
                        {icon ? (
                            icon
                        ) : isOutbound ? (
                            <OutboundPaymentIcon />
                        ) : (
                            <InboundPaymentIcon />
                        )}
                    </div>
                    <div className="text">
                        <div className="title">{title}</div>
                        <div className="subtitle">{subtitle}</div>
                    </div>
                    {chevronImage}
                </div>
            </div>
            <Moment calendar={calendarStrings}>{message.createdDate}</Moment>
        </div>
    )
}
